// An invoice's Amount note (DD-041): what arrived and what is still due, the server's figures (TD-102).
import { formatMoney } from "@/utils/currency";

export type FlowState = "in_progress" | "to_send" | "to_close" | "sent" | "overdue" | "partial" | "paid" | "written_off" | "closed" | "void";

/**
 * Whether an Amount column notes what arrived: any row with money in and not paid in full. That is a part payment, and
 * an overdue invoice whose payments only paid the ads back (TD-088), so the row says why it is less than the amount due.
 * A Paid row whose work was written off (TD-073) says it too, none included: settled is not paid in full.
 */
export const paidNoted = (paidMinor: number, state: FlowState, workWrittenOff = false): boolean =>
  workWrittenOff || (paidMinor > 0 && state !== "paid");

const dueText = (minor: number, currency: string): string => `${formatMoney(minor, currency)} due`;

/**
 * The Amount column's note: what arrived on any row paidNoted keeps, and on an overdue or partly paid invoice what is
 * still due too, the figure the digest prints (blockers._still_due). Null when the row notes nothing.
 */
export function amountNote(paidMinor: number, dueMinor: number | null, state: FlowState, currency: string, workWrittenOff = false): string | null {
  if (!paidNoted(paidMinor, state, workWrittenOff)) return null;
  const paid = `${formatMoney(paidMinor, currency)} paid`;
  return dueMinor !== null && (state === "overdue" || state === "partial") ? `${paid} · ${dueText(dueMinor, currency)}` : paid;
}

/**
 * A sent invoice's Amount note (review m2): what arrived and what is still due once anything arrived, as a partly paid
 * job's note reads, else what is still due alone. Both the server's figures (TD-102).
 */
export const invoiceDueNote = (receivedMinor: number, dueMinor: number, currency: string): string =>
  amountNote(receivedMinor, dueMinor, "partial", currency) ?? dueText(dueMinor, currency);
